import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { get } from 'lodash';
import { useRouter } from 'next/router';
import { FC, useActionState, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Swiper, SwiperSlide } from 'swiper/react';
import { styles as buttonStyles } from '~/components/Button';
import { styles as textStyles } from '~/components/Text';
import { GET_SLIDER_GQL } from '~/data/home/gql';
import { replaceClassNames } from '~/utils/converters';
import styles from './Hero.module.scss';
import { useRecoilState } from 'recoil';
import { cmsBlocksState } from '~/services/blocks';
import { Identifiers } from '~/constants/enum';
import useClient from '~/hooks/useClient';

interface HeroItem {
    id: string;
    html: string;
    image: string | null;
    imageMobile: string | null;
    content: string | null;
    actions: string[];
    action: string[];
}

interface HeroProps {}

const Hero: FC<HeroProps> = ({}) => {
    const isClient = useClient();
    const [cmsBlocks, setCmsBlocks] = useRecoilState(cmsBlocksState);
    const fetchSlider = useQuery(GET_SLIDER_GQL, {
        nextFetchPolicy: 'network-only',
        context: {
            fetchOptions: { method: 'GET' }
        },

        onCompleted: (data) => {
            setCmsBlocks((prev) => {
                return {
                    ...prev,
                    ['slider']: {
                        data: data?.getSlider,
                        timestamp: new Date().getTime()
                    }
                };
            });
        },
        skip: !!cmsBlocks['slider']
    });

    const _data: HeroItem[] = useMemo(() => {
        if (!fetchSlider.loading && cmsBlocks['slider']) {
            const sliderItem: any = get(cmsBlocks, 'slider.data.items', []);
            return sliderItem?.map((slider: any) => {
                return {
                    id: slider.id,
                    html: slider.html
                };
            });
        }

        if (!!fetchSlider.loading) {
            return [];
        }

        const sliderItem = get(fetchSlider, 'data.getSlider.items', []);

        return sliderItem?.map((slider: any) => {
            return {
                id: slider.id,
                html: slider.html
            };
        });
    }, [fetchSlider]);

    if (!isClient) return <Skeleton className={classNames('w-full h-[75vh]')} />;

    if (fetchSlider.loading) {
        return <Skeleton className={classNames('w-full h-[75vh]')} />;
    }

    if (_data.length === 0) {
        return <div>hero</div>;
    }

    return (
        <section className={styles.hero}>
            <Swiper
                {...{
                    autoplay: true,
                    infinite: _data.length > 1 ? 'true' : 'false'
                }}
            >
                {_data.map((item, index) => (
                    <SwiperSlide key={index}>
                        <div
                            className={styles.hero_item}
                            dangerouslySetInnerHTML={{
                                __html: replaceClassNames(item.html, {
                                    Hero: styles,
                                    Button: buttonStyles,
                                    Text: textStyles
                                })
                            }}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        </section>
    );
};

export default Hero;
